import React, { forwardRef } from 'react';
import Label from './Label';
import HelperText from './HelperText';

type Option = {
  label: string;
  value: string;
};

type Props = {
  label?: string | React.ReactNode;
  options?: Option[];
  helperText?: string;
  status?: 'error' | 'success';
  value?: string[];
  onChange?: (value: string[]) => void;
  disabled?: boolean;
  required?: boolean;
  optional?: boolean;
  labelHelperText?: string;
  id?: string;
  dataTestId?: string;
  name?: string;
};

const PillCheckboxGroup = forwardRef<HTMLDivElement, Props>(
  (
    {
      label,
      options = [],
      helperText,
      status,
      value = [],
      onChange,
      disabled,
      required,
      optional,
      labelHelperText,
      id,
      dataTestId,
    },
    ref
  ) => {
    const toggle = (optValue: string) => {
      if (disabled) return;
      const current = value || [];
      const updated = current.includes(optValue)
        ? current.filter((v) => v !== optValue)
        : [...current, optValue];
      onChange?.(updated);
    };

    return (
      <div className="flex flex-col" id={id} ref={ref} data-testid={dataTestId}>
        {label && (
          <Label
            text={label}
            htmlFor={id}
            required={required}
            optional={optional}
            labelHelperText={labelHelperText}
          />
        )}
        <div className="flex flex-wrap gap-2 mt-1">
          {options.map((opt) => {
            const isSelected = (value || []).includes(opt.value);
            return (
              <button
                key={opt.value}
                type="button"
                disabled={disabled}
                onClick={() => toggle(opt.value)}
                className={`px-4 py-[6px] border rounded-xl text-sm font-normal transition-all duration-200 focus:outline-none ${
                  isSelected
                    ? 'bg-light-success dark:bg-dark-success border-light-success dark:border-dark-success text-white shadow-sm font-medium'
                    : 'bg-white dark:bg-dark-surface-secondary border-light-input-border dark:border-dark-input-border text-neutral-700 dark:text-dark-text-secondary hover:bg-neutral-50 dark:hover:bg-neutral-800'
                } ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
              >
                {opt.label}
              </button>
            );
          })}
        </div>
        {helperText && <HelperText text={helperText} status={status} />}
      </div>
    );
  }
);

PillCheckboxGroup.displayName = 'PillCheckboxGroup';

export default PillCheckboxGroup;
